import { PRODUCT_NAME } from "@src/shared/brand";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Logo } from "@src/shared/components/general/Logo";
import { Text } from "@src/shared/components/general/Text";
import {
  Flexbox,
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { getAssetUrl } from "@src/shared/utils/assets/getAssetUrl";

export interface LoginLayoutProps {
  children: React.ReactNode;
}

/**
 * Split-screen layout for all unauthenticated pages. Image left, branded
 * content and form right.
 */
export const LoginLayout = ({ children }: LoginLayoutProps) => {
  return (
    <VerticalFlexbox className="min-h-screen bg-background">
      <Flexbox className="flex-1 overflow-hidden">
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="relative hidden w-1/2 overflow-hidden bg-gradient-to-br from-[hsl(var(--stitch-blue))] via-[hsl(228_55%_24%)] to-[hsl(195_77%_25%)] lg-stitch:flex"
        >
          <img
            src={getAssetUrl("/images/doctor-patient-2.jpg")}
            alt="Doctor and Patient"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--stitch-blue))]/85 via-[hsl(228_55%_20%)]/80 to-[hsl(195_77%_22%)]/80" />
          <VerticalFlexbox
            gap="sm"
            className="relative z-10 max-w-md px-10 text-white"
          >
            <Text
              as="span"
              sizing="sm"
              fontWeight={600}
              className="uppercase tracking-[0.2em] text-white"
            >
              {PRODUCT_NAME.singular}
            </Text>
            <HeaderText tag="h2" className="text-3xl leading-tight text-white">
              Beautifully coordinated care — without the busywork.
            </HeaderText>
            <Text className="text-white/90">
              {PRODUCT_NAME.singular} is the clinical copilot that keeps every
              patient thread together, so you can focus on practicing medicine.
            </Text>
          </VerticalFlexbox>
        </Flexbox>
        <VerticalFlexbox
          alignItems="center"
          justifyContent="space-between"
          gap="lg"
          className="w-full flex-1 overflow-y-auto px-6 py-10 lg-stitch:w-1/2 lg-stitch:px-12"
        >
          <HorizontalFlexbox
            alignItems="center"
            gap="sm"
            className="w-full max-w-md"
          >
            <Logo className="size-9" />
            <Text as="span" sizing="lg" fontWeight={600}>
              {PRODUCT_NAME.singular}
            </Text>
          </HorizontalFlexbox>
          <Flexbox alignItems="center" className="w-full max-w-md flex-1">
            {children}
          </Flexbox>
          <Text sizing="xs" className="text-foreground/60">
            &copy; 2025 Elation Health Inc. All rights reserved.
          </Text>
        </VerticalFlexbox>
      </Flexbox>
    </VerticalFlexbox>
  );
};
